import { type ComponentType, type CSSProperties, type RefObject, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
    AppWindowIcon,
    AutoAwesome,
    CirclePlayIcon,
    FunnelIcon,
    HouseIcon,
    InboxIcon,
    LayoutGridIcon,
    MenuIcon,
    PencilIcon,
    SearchIcon,
    SparkleIcon,
    StarOutlineOutlined,
    type SvgIconComponent,
    TimerIcon,
    XIcon,
} from 'components/icons';
import { Button } from 'components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { Tooltip } from 'components/ui/tooltip';
import { AiHero } from 'components/ai/AiHero';
import { useUiDesign } from 'hooks/useUiDesign';
import { useGetUserDataQuery } from 'store/api/user';
import PinDragDemo from 'tour/PinDragDemo';
import { useTour } from 'tour/TourProvider';
import WidgetArrangeDemo from 'tour/WidgetArrangeDemo';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { FOCUS_RING } from './focusRing';

// Two to a row, related tiles side by side, and one to a row under 768px. A tile with a tour demo ends its text with
// an icon that shows the demo in a card while hovered or focused.
const WHAT_IS_NEW: [string, SvgIconComponent, ComponentType?][] = [
    ['design.welcome.sidebar', MenuIcon],
    ['design.welcome.pins', StarOutlineOutlined, PinDragDemo],
    ['design.welcome.search', SearchIcon],
    ['design.welcome.shell', TimerIcon],
    ['design.welcome.inbox', InboxIcon],
    ['design.welcome.filters', FunnelIcon],
    ['design.welcome.home', HouseIcon],
    ['design.welcome.widgets', LayoutGridIcon, WidgetArrangeDemo],
    ['design.welcome.tabs', AppWindowIcon],
    ['design.welcome.edit', PencilIcon],
];

// A part of the staged entrance (texts reveal): it fades in, rising 12px out of a 3px blur, over 500ms; under reduced
// motion it only fades, in 150ms. From `@starting-style`, so nothing waits for a script: a part is never left unseen.
const PART =
    'transition-[opacity,translate,filter] duration-(--duration-very-slow) ease-(--ease-smooth-out) starting:opacity-0 motion-safe:starting:translate-y-(--distance-medium) motion-safe:starting:blur-(--blur-medium) motion-reduce:duration-150';

type Props = {
    /**
     * The New design row, which glows when the Welcome closes without the tour to show where the switch is. None
     * where the row is not in the page, as in the phone's closed drawer.
     */
    anchor?: RefObject<HTMLElement | null>;
    /** Opened again on demand, once seen: it saves nothing and leaves the row unlit. */
    reopened?: boolean;
    /** Called as it closes, whichever way. */
    onClose?: () => void;
    /** Where focus goes as it closes, when not back to where it came from. */
    returnFocus?: RefObject<HTMLElement | null>;
};

/**
 * Opens once by itself, the first time the new design shows: what is new, and the guided tour on offer. It counts
 * as seen as it shows, not as it closes: a tab closed with it still open would otherwise leave it unseen, and every
 * other tab, a link opened in a new one included, would show it again. Under 768px, which a desktop window zoomed
 * to 200% is too, it fills the screen: the hero as tall as its text, the list alone scrolling, the offer over its
 * two buttons. On a viewport too short to leave the list room (`short:`, which that zoomed window is as well) the
 * hero scrolls away with the list, and the offer stays at the bottom.
 *
 * It comes in by parts, 80ms apart: the title, the intro, what is new, then the tour's offer, each rising 12px out
 * of a blur as it fades in. The dialog itself keeps the kit's own enter and exit. Under reduced motion the parts only
 * fade, in 150ms, and nothing moves.
 */
function WelcomePopover({ anchor, reopened = false, onClose, returnFocus }: Props) {
    const { welcomeSeen, markWelcomeSeen } = useUiDesign();
    const { data: user } = useGetUserDataQuery();
    const { start } = useTour();
    const popupRef = useRef<HTMLDivElement>(null);
    // Its own state, read once: saving seen as it shows must not close it.
    const [first, setFirst] = useState(!welcomeSeen);
    const shown = first || reopened;

    useEffect(() => {
        if (first) void markWelcomeSeen();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- once as it shows; markWelcomeSeen is new every render
    }, [first]);

    const hide = () => {
        setFirst(false);
        onClose?.();
    };

    // A save that failed as it showed is tried again: the toast said why.
    const close = () => {
        if (first) {
            anchor?.current?.setAttribute('data-welcome-glow', '');
            if (!welcomeSeen) void markWelcomeSeen();
        }
        hide();
    };

    // The first time, seen is saved once more before the tour starts: the tour lets no write through, and the save
    // as it showed may still be on its way. A failed save keeps the Welcome open (the toast says why), so the tour
    // waits for the next try rather than starting under it.
    const takeTour = async () => {
        if (!first || (await markWelcomeSeen())) {
            hide();
            await start('welcome');
        }
    };

    return (
        <Dialog open={shown} onOpenChange={(next) => !next && close()}>
            <DialogContent
                showCloseButton={false}
                overlayProps={{
                    className: 'new:bg-welcome-scrim new:backdrop-blur-[2px]',
                }}
                // Under 768px its top edge is the screen's.
                className="new:shadow-welcome-dialog short:overflow-y-auto flex max-h-[calc(100dvh-2rem)] w-180 flex-col gap-0 overflow-hidden rounded-[18px] p-0 ring-0 max-sm:top-0 max-sm:h-dvh max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:translate-y-0 max-sm:rounded-none sm:max-w-180"
                // Focus the dialog, not ×, so it opens without a focus ring drawn on a button.
                ref={popupRef}
                initialFocus={popupRef}
                finalFocus={returnFocus ?? true}
            >
                <AiHero
                    size="welcome"
                    eyebrow={t('design.new-design')}
                    greeting={
                        user?.firstName
                            ? t('design.welcome-title', { name: user.firstName })
                            : t('design.welcome-title-no-name')
                    }
                    greetingAs={DialogTitle}
                    line={t('design.welcome-intro')}
                    lineAs={DialogDescription}
                    rowClassName={PART}
                    lineClassName={cn(PART, 'delay-(--duration-micro)')}
                    markClassName="welcome-mark"
                    markChildren={[0, 60, 120, 180, 240, 300].map((angle) => (
                        <SparkleIcon
                            key={angle}
                            fill="currentColor"
                            stroke="none"
                            className="welcome-burst absolute size-2 opacity-0"
                            style={{ '--welcome-burst-angle': `${angle}deg` } as CSSProperties}
                        />
                    ))}
                >
                    <DialogClose
                        aria-label={t('close')}
                        className="bg-welcome-hero-ink/10 text-welcome-hero-ink/80 hover:bg-welcome-hero-ink/20 absolute end-3.5 top-3.5 flex size-8 items-center justify-center rounded-md"
                    >
                        <XIcon className="size-4" />
                    </DialogClose>
                </AiHero>
                {/* Only what is new scrolls, on a viewport too short for it all. `!`: under `short:` it keeps its
                    full height whatever `max-sm:flex-1` says, and the dialog scrolls instead. */}
                <div
                    className={cn(
                        PART,
                        'short:flex-none! short:overflow-visible flex min-h-0 flex-col gap-4 overflow-y-auto overscroll-contain px-10 pt-6 pb-[18px] delay-[calc(var(--duration-micro)*2)] max-sm:flex-1 max-sm:px-5 max-sm:pt-5 max-sm:pb-4'
                    )}
                >
                    <ul className="grid grid-cols-2 gap-x-7 gap-y-2.5 max-sm:grid-cols-1">
                        {WHAT_IS_NEW.map(([id, Icon, Demo]) => (
                            <li key={id} className="flex min-h-12 items-center gap-3">
                                <span className="bg-primary-tint text-primary flex size-9 shrink-0 items-center justify-center rounded-[10px]">
                                    <Icon className="size-[18px]" />
                                </span>
                                <span className="text-foreground text-body1 leading-[1.4] text-pretty">
                                    {/* `pretty` leaves a last word alone on its line when an icon follows it, so the
                                        last two words are joined. */}
                                    {Demo ? t(id).replace(/ (?=\S+$)/, '\u00a0') : t(id)}
                                    {Demo && (
                                        <Tooltip
                                            content={
                                                // The demos are drawn for a 320px box: a narrower screen shows the
                                                // box whole at nine tenths rather than cut on the right.
                                                <div className="w-80 max-[352px]:[zoom:0.9]">
                                                    <Demo />
                                                </div>
                                            }
                                            className="max-w-none rounded-[12px] p-2"
                                            closeOnClick={false}
                                        >
                                            <button
                                                type="button"
                                                aria-label={t('design.welcome.demo')}
                                                className={cn(
                                                    'text-primary hit-area relative -my-0.5 ms-1 inline-flex size-5 cursor-pointer items-center justify-center rounded-sm align-middle',
                                                    FOCUS_RING
                                                )}
                                            >
                                                <CirclePlayIcon className="size-4" />
                                            </button>
                                        </Tooltip>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <span className="text-meta text-muted-foreground flex items-center gap-2 text-pretty">
                        {/* The New design switch, drawn on. */}
                        <span aria-hidden className="bg-secondary relative h-[18px] w-[30px] shrink-0 rounded-[9px]">
                            <span className="bg-card shadow-segment absolute start-3.5 top-0.5 size-3.5 rounded-full" />
                        </span>
                        {t('design.welcome-switch-back')}
                    </span>
                </div>
                {/* One row from 768px up, where the two wrappers are `contents`; under it the offer sits over its two
                    buttons, each half the width. */}
                <div
                    className={cn(
                        PART,
                        'border-primary-wash-border bg-primary-wash short:sticky short:bottom-0 flex shrink-0 items-center gap-5 border-t px-10 pt-[18px] pb-[22px] delay-[calc(var(--duration-micro)*3)] max-sm:flex-col max-sm:items-stretch max-sm:gap-3.5 max-sm:px-5 max-sm:pt-4 max-sm:pb-5'
                    )}
                >
                    <div className="flex items-start gap-3 sm:contents">
                        <AutoAwesome className="text-primary size-[26px]" />
                        <p className="text-foreground-secondary text-body1 flex-1 leading-[1.45] text-pretty">
                            <FormattedMessage
                                id="tour.offer"
                                values={{
                                    b: (chunks) => (
                                        <strong key="b" className="font-semibold">
                                            {chunks}
                                        </strong>
                                    ),
                                }}
                            />
                        </p>
                    </div>
                    <div className="flex gap-2 sm:contents">
                        <Button
                            variant="outline"
                            size="lg"
                            className="border-grey-300 bg-card text-foreground px-4 shadow-none max-sm:flex-1"
                            onClick={() => close()}
                        >
                            {t('tour.skip')}
                        </Button>
                        <Button
                            variant="secondary"
                            size="lg"
                            className="welcome-cta shadow-welcome-cta relative overflow-hidden px-[18px] font-semibold max-sm:flex-1"
                            onClick={() => void takeTour()}
                        >
                            <span className="relative">{t('tour.take')}</span>
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default WelcomePopover;
